import {
  ProjectMemberRole,
  Task,
  TaskMemberRole,
  TeamMemberRole,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { getProjectMembership } from '@hooks/useProjectMembership';

export type TaskMembership = {
  isMember: boolean;
  isOwner: boolean;
  isMemberOrOwner: boolean;
  isAssignee: boolean;
  // can do anything to task
  canEditTaskTopLevel: boolean;
  // can do anything to task without delete
  canEditTaskSecondLevel: boolean;
  // can do anything to task without delete without change member permission
  canEditTaskThirdLevel: boolean;
  // can view task members tab
  canViewTaskMembers: boolean;
  // can view task
  canViewTask: boolean;
  isTeamExpired: boolean;
};

export const getTaskMembership = (
  task: Pick<
    Task,
    | 'userIds'
    | 'creator'
    | 'currentUserProjectRole'
    | 'currentUserRole'
    | 'currentUserProjectTeamRole'
    | 'project'
  >,
  currentUserId?: string
): TaskMembership => {
  const isMember = !!task?.userIds?.some((id) => id === currentUserId);
  const isOwner = task?.currentUserRole === TaskMemberRole.Owner;
  const isMemberOrOwner = isMember || isOwner;
  const isAssignee = task?.currentUserRole === TaskMemberRole.Assignee;

  const { isTeamExpired } = getProjectMembership(
    task?.project,
    currentUserId ?? ''
  );

  const canEditTaskTopLevel =
    task?.currentUserRole === TaskMemberRole.Owner ||
    task?.currentUserProjectRole === ProjectMemberRole.Owner ||
    task?.currentUserProjectRole === ProjectMemberRole.Admin ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Owner ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Admin;

  const canEditTaskSecondLevel =
    canEditTaskTopLevel || task?.currentUserRole === TaskMemberRole.Assignee;

  const canEditTaskThirdLevel =
    canEditTaskTopLevel ||
    task?.currentUserRole === TaskMemberRole.Assignee ||
    task?.currentUserProjectRole === ProjectMemberRole.Editor ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Member;

  const canViewTaskMembers =
    canEditTaskSecondLevel || task?.currentUserRole === TaskMemberRole.Viewer;

  const canViewTask =
    canViewTaskMembers ||
    task?.currentUserProjectRole === ProjectMemberRole.Viewer ||
    task?.currentUserProjectRole === ProjectMemberRole.Editor ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Member;

  return {
    isMember,
    isOwner,
    isMemberOrOwner,
    isAssignee,
    canEditTaskTopLevel: canEditTaskTopLevel,
    canEditTaskSecondLevel: canEditTaskSecondLevel,
    canEditTaskThirdLevel,
    canViewTaskMembers,
    canViewTask,
    isTeamExpired,
  };
};

// Same rule as getTaskMembership(...).canEditTaskThirdLevel, but only needs
// the three role scalars (already server-scoped to the current user) —
// callers whose Task selection doesn't carry the full `project` object
// (e.g. MyTasksGroupTaskFragment) can use this instead of widening their
// query just to satisfy getProjectMembership's Pick<Project, ...>.
export const canEditTaskThirdLevel = (
  task: Pick<
    Task,
    'currentUserRole' | 'currentUserProjectRole' | 'currentUserProjectTeamRole'
  >
): boolean => {
  const canEditTaskTopLevel =
    task?.currentUserRole === TaskMemberRole.Owner ||
    task?.currentUserProjectRole === ProjectMemberRole.Owner ||
    task?.currentUserProjectRole === ProjectMemberRole.Admin ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Owner ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Admin;

  return (
    canEditTaskTopLevel ||
    task?.currentUserRole === TaskMemberRole.Assignee ||
    task?.currentUserProjectRole === ProjectMemberRole.Editor ||
    task?.currentUserProjectTeamRole === TeamMemberRole.Member
  );
};

// Whether the current user may only look at a task's files, with no edit,
// delete or share affordances. Being a task Viewer is not enough on its own:
// privilege on the owning project or its team outranks the task role.
//
// Five preview surfaces carried their own copy of this rule and they had
// drifted (TA-10701). AllTaskFiles omitted the project escape entirely, so a
// project Editor who happened to be a task Viewer lost edit and delete on the
// same file depending on which screen they opened it from. This is the rule
// the web copy already used, which is the most complete of the five.
//
// Takes only the three role scalars — already server-scoped to the current
// user — so callers whose Task selection has no project object can use it
// without widening their query.
export const isTaskFileViewerOnly = (
  task?: Pick<
    Task,
    'currentUserRole' | 'currentUserProjectRole' | 'currentUserProjectTeamRole'
  > | null
): boolean => {
  if (!task) return false;
  if (task.currentUserRole !== TaskMemberRole.Viewer) return false;

  const isProjectEditorOrHigher =
    task.currentUserProjectRole === ProjectMemberRole.Owner ||
    task.currentUserProjectRole === ProjectMemberRole.Admin ||
    task.currentUserProjectRole === ProjectMemberRole.Editor;

  const isOnProjectTeam =
    task.currentUserProjectTeamRole === TeamMemberRole.Owner ||
    task.currentUserProjectTeamRole === TeamMemberRole.Admin ||
    task.currentUserProjectTeamRole === TeamMemberRole.Member;

  return !isProjectEditorOrHigher && !isOnProjectTeam;
};

export const useTaskMembership = (
  task?: Pick<
    Task,
    | 'userIds'
    | 'creator'
    | 'currentUserProjectRole'
    | 'currentUserRole'
    | 'currentUserProjectTeamRole'
  >
): TaskMembership => {
  const { me } = useMe();
  return getTaskMembership(task!, me?.id);
};
